import React, { useState, useEffect, useRef } from 'react';
import { api } from '../api';
import HoursByPropertyChart from '../components/charts/HoursByPropertyChart';
import DatePicker from 'react-datepicker';
import { ChevronDown } from 'lucide-react'; // <-- Import the dropdown arrow icon

// Import the correct stylesheets
import 'react-datepicker/dist/react-datepicker.css';
import '../styles/DashboardScreen.css';


const LoadingSpinner = () => <div className="placeholder-text">Loading Chart Data...</div>;
const ErrorDisplay = ({ message }) => <div className="error-message">{message}</div>;


const DashboardScreen = ({ user }) => {
    // State for the chart data
    const [chartData, setChartData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // State for the filters
    const [properties, setProperties] = useState([]);
    const [selectedProperty, setSelectedProperty] = useState('all');
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [reportPeriod, setReportPeriod] = useState('');

    // State for the custom dropdown UI
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    // This function runs the report based on the current filter state
    const handleRunReport = async () => {
        setIsLoading(true);
        setError(null);

        const filters = {
            year: selectedDate.getFullYear(),
            month: selectedDate.getMonth() + 1,
            propertyId: selectedProperty
        };
        
        const monthYearTitle = selectedDate.toLocaleString('default', { month: 'long', year: 'numeric' });
        setReportPeriod(monthYearTitle);

        try {
            const data = await api.getDashboardReport(filters);
            setChartData(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    // This hook fetches the list of properties ONLY once when the component mounts.
    useEffect(() => {
        const fetchProperties = async () => {
            try {
                const props = await api.getProperties();
                setProperties(props);
            } catch (err) {
                console.error("Failed to fetch properties list:", err);
                setError('Could not load properties for filtering.');
            }
        };
        fetchProperties();
    }, []); // Empty array ensures this runs only once.

    // This hook automatically runs the report when a filter value changes.
    useEffect(() => {
        // We run the report on filter change. The initial report is also triggered by this
        // after the `selectedDate` state is initialized.
        handleRunReport();
    }, [selectedProperty, selectedDate]);


    // This hook handles closing the dropdown when you click outside of it.
    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        }
        // Add the event listener when the component mounts
        document.addEventListener("mousedown", handleClickOutside);
        // Clean up the event listener when the component unmounts
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [dropdownRef]);


    return (
        <div>
            <h1 className="page-title">Dashboard</h1>
            
            <div className="dashboard-filters">
                <div className="filter-group">
                    <label className="form-label">Property</label>
                    {/* This is our new custom dropdown component */}
                    <div className="custom-dropdown" ref={dropdownRef}>
                        <button 
                            type="button" 
                            className="form-input dropdown-button" 
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        >
                            <span>
                                {selectedProperty === 'all' 
                                    ? 'All Properties' 
                                    : (properties.find(p => p.id === parseInt(selectedProperty))?.name || 'All Properties')}
                            </span>
                            <ChevronDown size={16} className={`dropdown-chevron ${isDropdownOpen ? 'open' : ''}`} />
                        </button>

                        {isDropdownOpen && (
                            <ul className="dropdown-menu">
                                <li onClick={() => {
                                    setSelectedProperty('all');
                                    setIsDropdownOpen(false);
                                }}>
                                    All Properties
                                </li>
                                {properties.map(prop => (
                                    <li key={prop.id} onClick={() => {
                                        setSelectedProperty(prop.id.toString());
                                        setIsDropdownOpen(false);
                                    }}>
                                        {prop.name}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>

                <div className="filter-group">
                    <label className="form-label">Report Month</label>
                    <DatePicker
                        selected={selectedDate}
                        onChange={(date) => setSelectedDate(date)}
                        dateFormat="MMMM yyyy"
                        showMonthYearPicker
                        className="form-input"
                    />
                </div>
            </div>

            {isLoading && <LoadingSpinner />}
            {error && <ErrorDisplay message={error} />}
            {!isLoading && !error && (
                chartData && chartData.labels.length > 0
                    ? <HoursByPropertyChart chartData={chartData} reportPeriod={reportPeriod} />
                    : <div className="placeholder-text">No data found for the selected filters.</div>
            )}
        </div>
    );
};

export default DashboardScreen;